import { useEffect, useMemo, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { ExternalLink } from "lucide-react";
import { getConfig } from "@/src/config";
import { useOffersStore } from "@/src/stores/offersStore";
import { calculateCurrencyPrice } from "@/src/lib/utils";
import { Loader } from "./ChangeYachtDetailsModal/ChangeYachtDetailsModal";

/**
 * Booking-sheet view (prototype): the current search result set as a weekly
 * availability grid, à la Booking Manager. Rows are yachts, columns are
 * Saturday-to-Saturday weeks; cells are shaded free / on-option / booked and
 * carry the weekly price. Click a start then a finish week on a row to price
 * that exact period live (/pricing) and drop it into the offer. Availability is
 * read from the pre-warmed cache in one bulk call, so it stays cheap at scale.
 */

type Availability = {
  periodStart: string;
  periodEnd: string;
  available: boolean;
  onOption: boolean;
  optionExpiration?: string;
  clientPrice: number;
  listPrice: number;
};

type Item = {
  id: string;
  name?: string | null;
  slug?: string;
  media?: string[];
  provider?: string;
  // Fields the search response carries (some are absent from the FE Yacht type but
  // present at runtime — read defensively). Used for the sort + row subtitle.
  modelName?: string;
  boatType?: string;
  year?: number;
  cabins?: number;
  bathrooms?: number;
  maxPeople?: number;
  operator?: string;
  location?: string | string[];
  length?: number;
  countryCode?: string;
};

const CELL_W = 68; // px per week column

const COUNTRY_NAMES: Record<string, string> = {
  TR: "Turkey", GR: "Greece", HR: "Croatia", IT: "Italy", ES: "Spain", FR: "France",
  ME: "Montenegro", GB: "United Kingdom", US: "United States", VG: "British Virgin Islands",
  BS: "Bahamas", TH: "Thailand", SC: "Seychelles", MV: "Maldives", PF: "French Polynesia",
  AG: "Antigua", GD: "Grenada", MT: "Malta", PT: "Portugal", HR2: "Croatia",
};

const locStr = (y: Item) => (Array.isArray(y.location) ? y.location.filter(Boolean).join(", ") : y.location || "");
const countryOf = (y: Item) => (y.countryCode ? COUNTRY_NAMES[y.countryCode] || y.countryCode : "");

type SortField = {
  key: string;
  label: string;
  num?: boolean;
  get: (y: Item) => string | number;
  display: (y: Item) => string;
};
const SORT_FIELDS: SortField[] = [
  { key: "base", label: "Base", get: (y) => locStr(y).toLowerCase(), display: (y) => locStr(y) || "—" },
  { key: "country", label: "Country", get: (y) => countryOf(y).toLowerCase(), display: (y) => countryOf(y) || "—" },
  { key: "model", label: "Model", get: (y) => (y.modelName || "").toLowerCase(), display: (y) => y.modelName || "—" },
  { key: "type", label: "Type", get: (y) => (y.boatType || "").toLowerCase(), display: (y) => y.boatType || "—" },
  { key: "length", label: "Length", num: true, get: (y) => y.length || 0, display: (y) => (y.length ? `${y.length} m` : "—") },
  { key: "cabins", label: "Cabins", num: true, get: (y) => y.cabins || 0, display: (y) => (y.cabins != null ? `${y.cabins} cabins` : "—") },
  { key: "guests", label: "Guests", num: true, get: (y) => y.maxPeople || 0, display: (y) => (y.maxPeople ? `${y.maxPeople} guests` : "—") },
  { key: "year", label: "Year", num: true, get: (y) => y.year || 0, display: (y) => (y.year ? String(y.year) : "—") },
  { key: "company", label: "Operator", get: (y) => (y.operator || "").toLowerCase(), display: (y) => y.operator || "—" },
  { key: "name", label: "Yacht name", get: (y) => (y.name || "").toLowerCase(), display: (y) => locStr(y) || "—" },
];

export function CalendarView({
  items,
  openChangeDetails,
  onAddWithDates,
}: {
  items: Item[];
  openChangeDetails: (id: string) => void;
  onAddWithDates?: (id: string, from: string, to: string) => void;
}) {
  const { yachtsFilters, offerCurrencyCode, currencies } = useOffersStore();

  // Availability/pricing come back in EUR (search-normalised); render in the offer's
  // selected currency, converted like the yacht cards do.
  const code = offerCurrencyCode || "EUR";
  const eurCur = currencies?.find((c) => c.code === "EUR");
  const offerCur = currencies?.find((c) => c.code === code);
  const conv = (n: number) => (eurCur && offerCur ? calculateCurrencyPrice(eurCur, offerCur, n) : n);
  const eur = (n: number) =>
    new Intl.NumberFormat("en", { notation: "compact", style: "currency", currency: code, maximumFractionDigits: 1 }).format(conv(n));
  const eurFull = (n: number) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: code, maximumFractionDigits: 0 }).format(conv(n));
  const [rows, setRows] = useState<Record<string, Availability[]> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const ids = useMemo(() => (items ?? []).map((i) => i.id).filter(Boolean), [items]);
  const idKey = ids.join(",");

  useEffect(() => {
    if (!ids.length) {
      setRows({});
      return;
    }
    let cancelled = false;
    setRows(null);
    fetch(`${getConfig().SEARCH_URL}/availability-bulk?ids=${ids.join(",")}`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setRows(data ?? {});
      })
      .catch(() => {
        if (!cancelled) setRows({});
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idKey]);

  // The columns — every yacht shares the same Saturday periods, so use the first
  // populated row as the header.
  const weeks = useMemo(() => {
    if (!rows) return [];
    for (const id of ids) if (rows[id]?.length) return rows[id];
    return [];
  }, [rows, ids]);

  // Which column holds the search's check-in date (for auto-scroll + pre-select).
  const checkInIdx = useMemo(() => {
    if (!weeks.length || !yachtsFilters?.checkIn) return -1;
    const ci = yachtsFilters.checkIn;
    return weeks.findIndex((w) => w.periodStart.slice(0, 10) <= ci && ci < w.periodEnd.slice(0, 10));
  }, [weeks, yachtsFilters?.checkIn]);

  const checkOutIdx = useMemo(() => {
    if (!weeks.length || !yachtsFilters?.checkOut) return -1;
    const co = yachtsFilters.checkOut;
    return weeks.findIndex((w) => w.periodStart.slice(0, 10) < co && co <= w.periodEnd.slice(0, 10));
  }, [weeks, yachtsFilters?.checkOut]);

  // Open scrolled to the dates the broker already searched.
  useEffect(() => {
    if (checkInIdx >= 0 && scrollRef.current) {
      scrollRef.current.scrollLeft = Math.max(0, checkInIdx * CELL_W - CELL_W * 2);
    }
  }, [checkInIdx, weeks.length]);

  // Selection: {yachtId, a, b} column indices (inclusive). Second click sets the end.
  const [sel, setSel] = useState<{ id: string; a: number; b: number } | null>(null);
  const [priced, setPriced] = useState<{ loading: boolean; price?: number; listPrice?: number } | null>(null);

  const onCell = (id: string, idx: number, avail: Availability) => {
    if (!avail.available) return;
    setSel((prev) => {
      if (!prev || prev.id !== id) return { id, a: idx, b: idx };
      // second click on same row → set the far end (order it)
      return { id, a: Math.min(prev.a, idx), b: Math.max(prev.a, idx) };
    });
  };

  // Price the selected span live.
  useEffect(() => {
    if (!sel) {
      setPriced(null);
      return;
    }
    const from = weeks[sel.a]?.periodStart?.slice(0, 10);
    const to = weeks[sel.b]?.periodEnd?.slice(0, 10);
    if (!from || !to) return;
    let cancelled = false;
    setPriced({ loading: true });
    const guests = yachtsFilters?.people || 0;
    fetch(`${getConfig().SEARCH_URL}/pricing/${sel.id}?date_from=${from}&date_to=${to}${guests ? `&guests=${guests}` : ""}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        setPriced({ loading: false, price: d?.clientPrice ?? 0, listPrice: d?.listPrice ?? 0 });
      })
      .catch(() => !cancelled && setPriced({ loading: false, price: 0 }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel?.id, sel?.a, sel?.b]);

  // Month header spans.
  const monthSpans = useMemo(() => {
    const spans: { label: string; count: number }[] = [];
    for (const w of weeks) {
      const label = format(parseISO(w.periodStart), "MMM yyyy");
      const last = spans[spans.length - 1];
      if (last && last.label === label) last.count++;
      else spans.push({ label, count: 1 });
    }
    return spans;
  }, [weeks]);

  const byId = useMemo(() => {
    const m: Record<string, Item> = {};
    for (const it of (items ?? []) as Item[]) m[it.id] = it;
    return m;
  }, [items]);

  const [sortBy, setSortBy] = useState("base");
  const sortField = SORT_FIELDS.find((f) => f.key === sortBy) ?? SORT_FIELDS[0];
  const sortedIds = useMemo(() => {
    return [...ids].sort((a, b) => {
      const va = sortField.get(byId[a] ?? { id: a });
      const vb = sortField.get(byId[b] ?? { id: b });
      if (sortField.num) return (va as number) - (vb as number);
      return String(va).localeCompare(String(vb));
    });
  }, [ids, byId, sortBy]);

  if (rows === null) return <p className="text-sm text-slate-500 py-8 text-center">Loading availability…</p>;
  if (!ids.length) return <p className="text-sm text-slate-500 py-8 text-center">No yachts to show — adjust the filters.</p>;
  if (!weeks.length)
    return <p className="text-sm text-slate-500 py-8 text-center">No availability calendar for these yachts yet.</p>;

  const selName = sel ? byId[sel.id]?.name ?? "Yacht" : null;

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between mb-2 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          Click a start week, then a finish week on the same row to price it.
          <span className="ml-1 inline-block w-2.5 h-2.5 rounded-sm bg-white border border-slate-300 align-middle" /> Free
          <span
            className="ml-1 inline-block w-2.5 h-2.5 rounded-sm border border-amber-300 align-middle"
            style={{ background: "#fde68a" }}
          />{" "}
          On&nbsp;option
          <span
            className="ml-1 inline-block w-2.5 h-2.5 rounded-sm border border-slate-300 align-middle"
            style={{ backgroundImage: "repeating-linear-gradient(45deg, #cbd5e1 0, #cbd5e1 1px, #eef2f6 1px, #eef2f6 5px)" }}
          />{" "}
          Booked
        </span>
        <span className="inline-flex items-center gap-3">
          <label className="inline-flex items-center gap-1.5">
            <span className="text-slate-500">Sort by</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-7 rounded-md border border-slate-200 px-1.5 text-xs bg-white"
            >
              {SORT_FIELDS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          <span>{ids.length} yachts · weekly (Sat–Sat)</span>
        </span>
      </div>

      <div ref={scrollRef} className="overflow-x-auto border border-slate-200 rounded-lg">
        <div style={{ width: 200 + weeks.length * CELL_W }}>
          {/* Month header */}
          <div className="flex sticky top-0 z-20 bg-white border-b border-slate-200">
            <div className="w-[200px] shrink-0 sticky left-0 z-10 bg-white border-r border-slate-200" />
            {monthSpans.map((m, i) => (
              <div
                key={i}
                className="text-xs font-medium text-slate-600 border-r border-slate-100 py-1 px-2 whitespace-nowrap"
                style={{ width: m.count * CELL_W }}
              >
                {m.label}
              </div>
            ))}
          </div>
          {/* Week-start header */}
          <div className="flex sticky top-[25px] z-20 bg-white border-b border-slate-200">
            <div className="w-[200px] shrink-0 sticky left-0 z-10 bg-white border-r border-slate-200 text-xs text-slate-400 px-3 flex items-center">
              Yacht
            </div>
            {weeks.map((w, i) => (
              <div
                key={i}
                className={`text-[11px] text-center py-1 border-r border-slate-100 shrink-0 ${
                  i === checkInIdx ? "text-blue-700 font-semibold" : "text-slate-500"
                }`}
                style={{ width: CELL_W }}
              >
                {format(parseISO(w.periodStart), "d MMM")}
              </div>
            ))}
          </div>

          {/* Rows */}
          {sortedIds.map((id) => {
            const row = rows![id] ?? [];
            const it = byId[id];
            const subtitle = it ? sortField.display(it) : "";
            return (
              <div key={id} className="flex border-b border-slate-100 last:border-b-0">
                <div className="w-[200px] shrink-0 sticky left-0 z-10 bg-white border-r border-slate-200 flex items-center gap-2 px-2 py-1.5">
                  {it?.media?.[0] ? (
                    <img src={it.media[0]} alt="" className="w-9 h-7 rounded object-cover shrink-0" />
                  ) : (
                    <span className="w-9 h-7 rounded bg-slate-200 shrink-0" />
                  )}
                  <div className="min-w-0 flex flex-col">
                    <span className="flex items-center gap-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => openChangeDetails(id)}
                        className="text-xs font-medium text-left truncate hover:text-blue-700"
                        title={it?.name ?? ""}
                      >
                        {it?.name ?? "Yacht"}
                      </button>
                      {it?.slug && (
                        <a
                          href={`${getConfig().WEBSITE_URL}/yacht/${it.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="shrink-0 text-slate-400 hover:text-blue-600"
                          title="Open yacht page"
                        >
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </span>
                    <span className="text-[10px] text-slate-500 truncate" title={subtitle}>
                      {subtitle}
                    </span>
                  </div>
                </div>
                {weeks.map((_, i) => {
                  const cell = row[i];
                  const inSel = sel?.id === id && i >= sel.a && i <= sel.b;
                  const inSearch =
                    !sel && checkInIdx >= 0 && checkOutIdx >= 0 && i >= checkInIdx && i <= checkOutIdx;
                  const booked = !cell?.available && !cell?.onOption;
                  const hasDiscount = !!cell?.available && cell.listPrice > cell.clientPrice && cell.clientPrice > 0;
                  let cls = "bg-white hover:bg-emerald-50 cursor-pointer";
                  if (cell?.onOption) cls = "bg-amber-100 cursor-pointer";
                  else if (booked) cls = "cursor-default";
                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={!cell?.available}
                      onClick={() => cell && onCell(id, i, cell)}
                      title={booked ? "Booked" : cell?.onOption ? "On option" : "Available"}
                      className={`relative shrink-0 border-r border-slate-100 text-[10px] leading-none text-center py-1.5 ${cls} ${
                        inSel ? "ring-2 ring-blue-500 ring-inset !bg-blue-50 z-[1]" : inSearch ? "bg-blue-50/70" : ""
                      }`}
                      style={
                        booked
                          ? {
                              width: CELL_W,
                              backgroundImage:
                                "repeating-linear-gradient(45deg, #cbd5e1 0, #cbd5e1 1px, #eef2f6 1px, #eef2f6 6px)",
                            }
                          : { width: CELL_W }
                      }
                    >
                      {cell?.available && cell.clientPrice > 0 && (
                        <span className="flex flex-col items-center gap-0.5">
                          <span className="text-slate-700 font-medium">{eur(cell.clientPrice)}</span>
                          {hasDiscount && (
                            <span className="text-[8px] text-slate-400 line-through">{eur(cell.listPrice)}</span>
                          )}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected-period price panel */}
      {sel && (
        <div className="mt-3 flex items-center gap-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5">
          <div className="flex flex-col">
            <span className="text-sm font-semibold">{selName}</span>
            <span className="text-xs text-slate-500">
              {format(parseISO(weeks[sel.a].periodStart), "d MMM")} – {format(parseISO(weeks[sel.b].periodEnd), "d MMM yyyy")}
              {sel.b > sel.a ? ` · ${sel.b - sel.a + 1} weeks` : " · 1 week"}
            </span>
          </div>
          <div className="ml-auto text-right">
            {priced?.loading ? (
              <Loader text="Pricing…" />
            ) : (
              <div className="flex flex-col items-end leading-tight">
                <span className="text-lg font-semibold">{priced?.price ? eurFull(priced.price) : "—"}</span>
                {!!priced?.listPrice && priced.listPrice > (priced.price ?? 0) && (
                  <span className="text-xs text-slate-400 line-through">{eurFull(priced.listPrice)}</span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              const from = weeks[sel.a].periodStart.slice(0, 10);
              const to = weeks[sel.b].periodEnd.slice(0, 10);
              if (onAddWithDates) onAddWithDates(sel.id, from, to);
              else openChangeDetails(sel.id);
            }}
            className="rounded-md bg-blue-600 text-white text-sm font-medium px-3 py-1.5 hover:bg-blue-700"
          >
            Add to offer
          </button>
          <button type="button" onClick={() => setSel(null)} className="text-slate-400 hover:text-slate-700 text-sm">
            Clear
          </button>
        </div>
      )}
    </div>
  );
}
